import React, { useEffect, useId, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT, Swatch } from '../shared'

/** Extended finish grids — beyond the quick inspector swatches. */
export const WALL_FINISH_EXTENDED = [
  '#40365a', '#1b1722', '#efece4', '#9aa88f', '#b06a48', '#2a3350',
  '#2c2438', '#4a3f5c', '#6b5f7a', '#8a7f94',
  '#d4cfc4', '#c4b8a8', '#a89888', '#7a6a5a',
  '#3a4558', '#4a5568', '#5a6a7a', '#8a9aaa',
  '#5a3a32', '#7a4a38', '#9a6a48', '#c48a68',
  '#1a3028', '#2a4838', '#3a6048', '#6a8870',
]

export const FLOOR_FINISH_EXTENDED = [
  '#241c33', '#5a4634', '#cfc8bb', '#6e6e6c',
  '#1a1424', '#2e2438', '#3a3048',
  '#3a2a1e', '#4a3828', '#6a5040', '#8a6a50',
  '#b8b0a4', '#a0988c', '#888078', '#504840',
  '#2a2a2e', '#3a3a40', '#5a5a60', '#8a8a90',
  '#3a2820', '#4a3428', '#6a4830',
]

export const CEIL_FINISH_EXTENDED = [
  '#1d1729', '#241a12', '#d7dcdb', '#0f0e14', '#3a3550',
  '#141221', '#2a2438', '#3e3652',
  '#241c30', '#382c1c', '#4a3a24', '#6a5638',
  '#c8cccb', '#e8e6de', '#f0eee6', '#a8aca8',
  '#1a1e28', '#242a38', '#343e50', '#586478',
  '#180f0c', '#241612', '#38241a',
]

/** Beyond the free Ivory/Chalk/Charcoal/Blush 4 — museum-board tones, deliberately more muted/neutral than the wall/floor palettes since matting sits right next to the photo. */
export const MAT_FINISH_EXTENDED = [
  '#e4ddcf', '#d8d2c4', '#c9c2b2', '#8f897d',
  '#4a463f', '#1a1814', '#0a0a0a',
  '#dfd6c8', '#cbb99e', '#a8967a', '#7a6a54',
  '#c9a89a', '#b4877a', '#8a5a4a', '#5a3a30',
  '#7a8478', '#5a6a5a', '#3a4238',
  '#7a8898', '#5a6878', '#3a4450',
  '#8a6a56', '#6a4a38',
]

const Sheet = styled(GlassPanel)({
  position: 'absolute',
  left: 8,
  right: 8,
  bottom: 8,
  zIndex: 20,
  padding: '12px 12px 11px',
  borderRadius: 12,
  boxShadow: '0 22px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(190,157,253,0.12)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  animation: 'finishSheetIn 160ms ease-out',
  '@keyframes finishSheetIn': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
})

const SheetHead = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
})

const SheetKind = styled(Box)({
  ...monoLabelSx,
  fontSize: 7.5,
  color: STUDIO_ACCENT.violet,
  marginBottom: 3,
})

const SheetTitle = styled(Typography)({
  fontFamily: 'var(--ds-font)',
  fontSize: 13.5,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-night-ink)',
  lineHeight: 1.2,
})

const CloseBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 26,
  height: 26,
  borderRadius: 7,
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  color: 'var(--ds-night-ink-subtle)',
  background: 'rgba(244,243,246,0.05)',
  border: '1px solid var(--ds-night-border)',
  fontSize: 14,
  lineHeight: 1,
  flexShrink: 0,
  transition: 'color 120ms ease, border-color 120ms ease',
  '&:hover': { color: 'var(--ds-night-ink)', borderColor: 'rgba(244,243,246,0.28)' },
})

const PreviewRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
})

const PreviewSwatch = styled(Box, { shouldForwardProp: (p) => p !== '$color' })<{ $color: string }>(({ $color }) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  flexShrink: 0,
  background: $color,
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.14), 0 4px 14px rgba(0,0,0,0.35)',
}))

const HexField = styled('input')({
  flex: 1,
  minWidth: 0,
  height: 32,
  padding: '0 10px',
  borderRadius: 8,
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.04)',
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 12,
  letterSpacing: '0.04em',
  outline: 'none',
  textTransform: 'uppercase',
  transition: 'border-color 120ms ease',
  '&:focus': { borderColor: 'rgba(190,157,253,0.55)' },
  '&::placeholder': { color: 'var(--ds-night-ink-subtle)', textTransform: 'none' },
})

const NativeTrigger = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 32,
  height: 32,
  borderRadius: 8,
  cursor: 'pointer',
  flexShrink: 0,
  background: 'conic-gradient(from 180deg, #fe6a6a, #ffcf7d, #7dffb3, #01dafe, #be9dfd, #fe6a6a)',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.22)',
  position: 'relative',
  transition: 'transform 120ms ease',
  '&:hover': { transform: 'scale(1.06)' },
})

const HiddenColor = styled('input')({
  position: 'absolute',
  opacity: 0,
  width: 0,
  height: 0,
  pointerEvents: 'none',
})

const GridLabel = styled(Typography)({
  ...monoLabelSx,
  fontSize: 7,
  marginBottom: 6,
})

const FinishGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  // Gap + padding leave room for the violet selection ring (box-shadow ~3.5px).
  gap: 8,
  maxHeight: 120,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(244,243,246,0.14) transparent',
  padding: 4,
  margin: -4,
})

const Actions = styled(Box)({
  display: 'flex',
  gap: 6,
  alignItems: 'center',
})

const GhostBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 28,
  padding: '0 11px',
  borderRadius: 7,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ds-night-ink-subtle)',
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.03)',
  transition: 'color 120ms ease, border-color 120ms ease',
  '&:hover': { color: 'var(--ds-night-ink)', borderColor: 'rgba(244,243,246,0.28)' },
})

const ApplyBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 28,
  padding: '0 14px',
  borderRadius: 7,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 700,
  color: '#0a0912',
  background: STUDIO_ACCENT.violet,
  border: '1px solid transparent',
  marginLeft: 'auto',
  transition: 'filter 120ms ease',
  '&:hover': { filter: 'brightness(1.08)' },
  '&:disabled': { opacity: 0.45, cursor: 'default', filter: 'none' },
})

const ProStrip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 9px',
  borderRadius: 8,
  background: 'linear-gradient(135deg, rgba(255,207,125,0.12), rgba(190,157,253,0.08))',
  border: '1px solid rgba(255,207,125,0.28)',
})

const ProBadge = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7,
  fontWeight: 700,
  letterSpacing: '0.1em',
  color: '#0a0912',
  background: STUDIO_ACCENT.gold,
  borderRadius: 3,
  padding: '2px 5px',
  lineHeight: 1.2,
  flexShrink: 0,
})

const ProCopy = styled(Typography)({
  flex: 1,
  minWidth: 0,
  fontSize: 10.5,
  lineHeight: 1.35,
  color: 'var(--ds-night-ink-subtle)',
  '& strong': { color: 'var(--ds-night-ink)', fontWeight: 650 },
})

const UpgradeLink = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: STUDIO_ACCENT.gold,
  flexShrink: 0,
  padding: '4px 2px',
  '&:hover': { textDecoration: 'underline' },
})

function normalizeHex(raw: string): string | null {
  const t = raw.trim()
  const withHash = t.startsWith('#') ? t : `#${t}`
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toLowerCase()
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const r = withHash[1]
    const g = withHash[2]
    const b = withHash[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  return null
}

export interface FinishColorPanelProps {
  surface: 'wall' | 'floor' | 'ceiling' | 'mat'
  initialColor: string
  showProStrip: boolean
  onApply: (hex: string) => void
  onClose: () => void
  onUpgrade: () => void
}

export function FinishColorPanel({
  surface,
  initialColor,
  showProStrip,
  onApply,
  onClose,
  onUpgrade,
}: FinishColorPanelProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const nativeRef = useRef<HTMLInputElement>(null)
  const seed =
    normalizeHex(initialColor) ??
    (surface === 'wall' ? '#40365a' : surface === 'floor' ? '#241c33' : surface === 'mat' ? '#e4ddcf' : '#1d1729')
  const [draft, setDraft] = useState(seed)
  const [hexText, setHexText] = useState(seed.toUpperCase())
  const palette =
    surface === 'wall'
      ? WALL_FINISH_EXTENDED
      : surface === 'floor'
        ? FLOOR_FINISH_EXTENDED
        : surface === 'mat'
          ? MAT_FINISH_EXTENDED
          : CEIL_FINISH_EXTENDED
  const valid = normalizeHex(hexText) !== null

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = panelRef.current
      if (!el) return
      const t = e.target
      if (!(t instanceof Node)) return
      if (el.contains(t)) return
      // Let the PRO swatch toggle handle its own click (avoid close→reopen race).
      if (t instanceof Element && t.closest('[data-finish-trigger]')) return
      onClose()
    }
    // Defer so the opening click doesn't immediately close.
    const timer = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('mousedown', onDown)
    }
  }, [onClose])

  const setColor = (hex: string) => {
    const n = normalizeHex(hex)
    if (!n) return
    setDraft(n)
    setHexText(n.toUpperCase())
  }

  return (
    <Sheet
      ref={panelRef}
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <SheetHead>
        <Box>
          <SheetKind>FINISH · {surface.toUpperCase()}</SheetKind>
          <SheetTitle id={titleId}>Custom color</SheetTitle>
        </Box>
        <CloseBtn type='button' aria-label='Close' onClick={onClose}>
          ×
        </CloseBtn>
      </SheetHead>

      <PreviewRow>
        <PreviewSwatch $color={draft} aria-hidden />
        <HexField
          value={hexText}
          spellCheck={false}
          aria-label='Hex color'
          placeholder='#40365A'
          onChange={(e) => {
            setHexText(e.target.value)
            const n = normalizeHex(e.target.value)
            if (n) setDraft(n)
          }}
          onBlur={() => {
            const n = normalizeHex(hexText)
            if (n) setHexText(n.toUpperCase())
            else setHexText(draft.toUpperCase())
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valid) {
              onApply(draft)
              onClose()
            }
          }}
        />
        <NativeTrigger
          type='button'
          title='Spectrum picker'
          aria-label='Open spectrum picker'
          onClick={() => nativeRef.current?.click()}
        />
        <HiddenColor
          ref={nativeRef}
          type='color'
          value={draft}
          onChange={(e) => setColor(e.target.value)}
        />
      </PreviewRow>

      <Box>
        <GridLabel>Studio palette</GridLabel>
        <FinishGrid>
          {palette.map((hex) => (
            <Swatch
              key={hex}
              color={hex}
              size={22}
              selected={draft.toLowerCase() === hex.toLowerCase()}
              onClick={() => setColor(hex)}
            />
          ))}
        </FinishGrid>
      </Box>

      <Actions>
        <GhostBtn type='button' onClick={onClose}>
          Cancel
        </GhostBtn>
        <ApplyBtn
          type='button'
          disabled={!valid}
          onClick={() => {
            onApply(draft)
            onClose()
          }}
        >
          Apply
        </ApplyBtn>
      </Actions>

      {showProStrip && (
        <ProStrip>
          <ProBadge>PRO</ProBadge>
          <ProCopy>
            <strong>Gallery+</strong> · Custom finishes beyond the curated set.
          </ProCopy>
          <UpgradeLink type='button' onClick={onUpgrade}>
            Upgrade
          </UpgradeLink>
        </ProStrip>
      )}
    </Sheet>
  )
}
